import { DayObject } from "@/types/date"
import { LoginAuthProvider } from "@/types/user"

export interface ErrorOptions {
  cause?: unknown
}

export const ClientExceptionTypeValues = [
  "ClientException",
  "ClientValidationException",
  "ClientRateLimitException",
  "ClientAuthException",
  "AuthenticationException",
  "ForbiddenException",
  "ProviderMismatchException",
  "ImmovableItineraryEvents",
  "ConfirmationRequired",
  "ItineraryEventsPreventFlexibleTrip",
  "UndeletableItineraryEvents",
  "ItineraryEventOutsideTimeFrame",
  "OutOfRangeBooking",
  "OutOfRangeBookingConfirmationRequired",
  "OutOfRangeBookingSetDatesConfirmationRequired",
  "StaleHotelRoomRecoveryRequired",
  "InvalidInviteException",
  "UnavailableShareException",
  "AcceptInvitationException",
  "SendInvitationException",
  "ThatchEmailMismatchException",
  "FlightSeatMapUnavailableException",
  "FlightDateOutOfRangeException",
  "FlightExpirationException",
  "FlightOfferUnavailableException",
  "FlightPassengersChangedException",
  "FlightUnableToPriceException",
  "FlightUnableToCheckoutException",
  "InvalidVerifyCodeException",
  "VerifyCodeExpiredException",
  "VerifyCodeAlreadyRedeemedException",
  "VerifyCodeNotFoundException",
  "VerifyCodeSignInExpiredException",
  "VerifyCodeNotRedeemedException",
  "AuthenticationFailedException",
  "SignInFailedException",
  "AlreadyRegisteredException",
  "InvalidTokenException",
  "OAuthProviderException",
  "ThatchVerificationFailedException",
  "ThatchAuthenticationException",
  "EmailAliasException",
] as const

export type ClientExceptionTypes = (typeof ClientExceptionTypeValues)[number]

export const MtExceptionTypeValues = [
  "UnknownError",
  "IgnoredError",
  "AuthTokenExpiredException",
  "AuthTokenInvalidException",
  "AuthFlowException",
  "LegacyNextAuthException",
  ...ClientExceptionTypeValues,
] as const

export type MtExceptionTypes = (typeof MtExceptionTypeValues)[number]

export abstract class MtException extends Error {
  constructor(name: MtExceptionTypes, message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = name
  }
}

export class ClientException<
  TData = unknown,
  TError extends MtExceptionTypes = "ClientException",
> extends MtException {
  readonly __error_type: TError
  message: string = "Unknown Client Exception"

  constructor(
    name: TError,
    message: string,
    public data?: TData,
    options?: ErrorOptions
  ) {
    super(name, message, options)
    this.message = message
    this.data = data
    this.__error_type = name
  }
}

export class ClientValidationException<TData = unknown> extends ClientException<
  TData,
  "ClientValidationException"
> {
  constructor(
    message: string,
    public data: TData,
    options?: ErrorOptions
  ) {
    super("ClientValidationException", message, data, options)
  }
}

export type ValidationResult = string[] | Record<string, string>

export type ValidationData<T> = {
  [K in keyof Partial<T>]: T[K] extends Record<string, unknown>
    ? ValidationData<T[K]>
    : T[K] extends Array<infer U>
      ? Record<
          number,
          U extends Record<string, unknown>
            ? ValidationData<U>
            : ValidationResult
        >
      : ValidationResult
}

export type ClientRateLimitExceptionData = {
  reason:
    | "anon_throttling"
    | "unverified_throttling"
    | "abuse_throttling"
    | "anon_per_trip_throttling"
}

export class ClientRateLimitException extends ClientException<
  ClientRateLimitExceptionData,
  "ClientRateLimitException"
> {
  constructor(
    message: string,
    public data: ClientRateLimitExceptionData,
    options?: ErrorOptions
  ) {
    super("ClientRateLimitException", message, data, options)
  }
}

export class DeprecatedClientAuthException extends ClientException<
  never,
  "ClientAuthException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("ClientAuthException", message, null as never, options)
  }
}

export class AuthenticationException extends ClientException<
  never,
  "AuthenticationException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("AuthenticationException", message, null as never, options)
  }
}

export class ForbiddenException extends ClientException<
  never,
  "ForbiddenException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("ForbiddenException", message, null as never, options)
  }
}

export class ProviderMismatchException extends ClientException<
  {
    attempted: LoginAuthProvider
    valid_providers: LoginAuthProvider[]
  },
  "ProviderMismatchException"
> {
  constructor(
    message: string,
    public data: {
      attempted: LoginAuthProvider
      valid_providers: LoginAuthProvider[]
    },
    options?: ErrorOptions
  ) {
    super("ProviderMismatchException", message, null as never, options)
  }
}

export class ImmovableItineraryEvents extends ClientException<
  number[],
  "ImmovableItineraryEvents"
> {
  constructor(
    message: string,
    public data: number[],
    options?: ErrorOptions
  ) {
    super("ImmovableItineraryEvents", message, data, options)
  }
}

export class ConfirmationRequired extends ClientException<
  { movable_event_ids: number[]; extendable_event_ids: number[] },
  "ConfirmationRequired"
> {
  constructor(
    message: string,
    public data: {
      movable_event_ids: number[]
      extendable_event_ids: number[]
    },
    options?: ErrorOptions
  ) {
    super("ConfirmationRequired", message, data, options)
  }
}

export class ItineraryEventsPreventFlexibleTrip extends ClientException<
  number[],
  "ItineraryEventsPreventFlexibleTrip"
> {
  constructor(
    message: string,
    public data: number[],
    options?: ErrorOptions
  ) {
    super("ItineraryEventsPreventFlexibleTrip", message, data, options)
  }
}

export class UndeletableItineraryEvents extends ClientException<
  number[],
  "UndeletableItineraryEvents"
> {
  constructor(
    message: string,
    public data: number[],
    options?: ErrorOptions
  ) {
    super("UndeletableItineraryEvents", message, data, options)
  }
}

export class ItineraryEventOutsideTimeFrame extends ClientException<
  number[],
  "ItineraryEventOutsideTimeFrame"
> {
  constructor(
    message: string,
    public data: number[],
    options?: ErrorOptions
  ) {
    super("ItineraryEventOutsideTimeFrame", message, data, options)
  }
}

export type OutOfRangeBookingData = {
  trip_start_date?: DayObject
  trip_end_date?: DayObject
}

export class OutOfRangeBooking extends ClientException<
  OutOfRangeBookingData | undefined,
  "OutOfRangeBooking"
> {
  constructor(
    message: string,
    data?: OutOfRangeBookingData,
    options?: ErrorOptions
  ) {
    super("OutOfRangeBooking", message, data, options)
  }
}

export type OutOfRangeBookingConfirmationRequiredData =
  OutOfRangeBookingData & {
    new_start_date: DayObject
    new_end_date: DayObject
  }

export class OutOfRangeBookingConfirmationRequired extends ClientException<
  OutOfRangeBookingConfirmationRequiredData | undefined,
  "OutOfRangeBookingConfirmationRequired"
> {
  constructor(
    message: string,
    data?: OutOfRangeBookingConfirmationRequiredData,
    options?: ErrorOptions
  ) {
    super("OutOfRangeBookingConfirmationRequired", message, data, options)
  }
}

export class OutOfRangeBookingSetDatesConfirmationRequired extends ClientException<
  OutOfRangeBookingConfirmationRequiredData | undefined,
  "OutOfRangeBookingSetDatesConfirmationRequired"
> {
  constructor(
    message: string,
    data?: OutOfRangeBookingConfirmationRequiredData,
    options?: ErrorOptions
  ) {
    super(
      "OutOfRangeBookingSetDatesConfirmationRequired",
      message,
      data,
      options
    )
  }
}

export class StaleHotelRoomRecoveryRequired extends ClientException<
  { reason?: string } | undefined,
  "StaleHotelRoomRecoveryRequired"
> {
  constructor(
    message: string,
    public data?: { reason?: string },
    options?: ErrorOptions
  ) {
    super("StaleHotelRoomRecoveryRequired", message, data, options)
  }
}

export class InvalidInviteException extends ClientException<
  never,
  "InvalidInviteException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("InvalidInviteException", message, null as never, options)
  }
}

export interface UnavailableShareExceptionData {
  reason:
    | "expired"
    | "disabled"
    | "max_users_reached"
    | "registration_required"
    | "wrong_type"
    | "no_content"
    | "group_chat_cannot_be_shared"
}

export class UnavailableShareException extends ClientException<
  UnavailableShareExceptionData,
  "UnavailableShareException"
> {
  constructor(
    message: string,
    public data: UnavailableShareExceptionData,
    options?: ErrorOptions
  ) {
    super("UnavailableShareException", message, data, options)
  }
}

export interface AcceptInvitationExceptionData {
  reason:
    | "expired"
    | "redeemed"
    | "disabled"
    | "max_users_reached"
    | "registration_required"
    | "email_mismatch"
  email_invited?: string
  affected_emails?: string[]
}

export class AcceptInvitationException extends ClientException<
  AcceptInvitationExceptionData,
  "AcceptInvitationException"
> {
  constructor(
    message: string,
    public data: {
      reason:
        | "expired"
        | "redeemed"
        | "disabled"
        | "max_users_reached"
        | "registration_required"
        | "email_mismatch"
      email_invited?: string
      affected_emails?: string[]
    },
    options?: ErrorOptions
  ) {
    super("AcceptInvitationException", message, data, options)
  }
}

export interface SendInvitationExceptionData {
  reason: "already_collaborating" | "already_invited"
}

export class SendInvitationException extends ClientException<
  SendInvitationExceptionData,
  "SendInvitationException"
> {
  constructor(
    message: string,
    public data: {
      reason: "already_collaborating" | "already_invited"
    },
    options?: ErrorOptions
  ) {
    super("SendInvitationException", message, data, options)
  }
}

export class ThatchEmailMismatchException extends ClientException<
  never,
  "ThatchEmailMismatchException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("ThatchEmailMismatchException", message, null as never, options)
  }
}

export class UnknownError extends MtException {
  data: unknown

  constructor(message: string, data?: unknown, options?: ErrorOptions) {
    super("UnknownError", message, options)
    this.data = data
  }
}

// this error will be ignored by Honeybadger
export class IgnoredError extends MtException {
  constructor(message: string, options?: ErrorOptions) {
    super("IgnoredError", message, options)
  }
}

export class AuthTokenExpiredException extends MtException {
  constructor(message = "AuthToken is expired.", options?: ErrorOptions) {
    super("AuthTokenExpiredException", message, options)
  }
}

export class AuthTokenInvalidException extends MtException {
  constructor(message = "AuthToken is invalid.", options?: ErrorOptions) {
    super("AuthTokenInvalidException", message, options)
  }
}

export class AuthFlowException extends MtException {
  constructor(message: string, options?: ErrorOptions) {
    super("AuthFlowException", message, options)
  }
}

export class LegacyNextAuthException extends MtException {
  constructor(message: string, options?: ErrorOptions) {
    super("LegacyNextAuthException", message, options)
  }
}

// ok I think best way forward is to have WrappedPromiseRejectionError
// then do our own window.onunhandledrejection to catch it
// turn off the HB one in the browser
// wrap non Errors in it
// if an error, just add a __promise_stack property
// in the add __promise_stack property to the context of the error
export class WrappedPromiseRejectionError extends Error {
  readonly name = "WrappedPromiseRejectionError"
}

export class InvalidVerifyCodeException extends ClientException<
  never,
  "InvalidVerifyCodeException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("InvalidVerifyCodeException", message, null as never, options)
  }
}

export class VerifyCodeExpiredException extends ClientException<
  never,
  "VerifyCodeExpiredException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("VerifyCodeExpiredException", message, null as never, options)
  }
}

export class VerifyCodeAlreadyRedeemedException extends ClientException<
  never,
  "VerifyCodeAlreadyRedeemedException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("VerifyCodeAlreadyRedeemedException", message, null as never, options)
  }
}

export class VerifyCodeNotFoundException extends ClientException<
  never,
  "VerifyCodeNotFoundException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("VerifyCodeNotFoundException", message, null as never, options)
  }
}

export class VerifyCodeSignInExpiredException extends ClientException<
  never,
  "VerifyCodeSignInExpiredException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("VerifyCodeSignInExpiredException", message, null as never, options)
  }
}

export class VerifyCodeNotRedeemedException extends ClientException<
  never,
  "VerifyCodeNotRedeemedException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("VerifyCodeNotRedeemedException", message, null as never, options)
  }
}

export class AuthenticationFailedException extends ClientException<
  never,
  "AuthenticationFailedException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("AuthenticationFailedException", message, null as never, options)
  }
}

export class SignInFailedException extends ClientException<
  never,
  "SignInFailedException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("SignInFailedException", message, null as never, options)
  }
}

export class AlreadyRegisteredException extends ClientException<
  never,
  "AlreadyRegisteredException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("AlreadyRegisteredException", message, null as never, options)
  }
}

export class InvalidTokenException extends ClientException<
  never,
  "InvalidTokenException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("InvalidTokenException", message, null as never, options)
  }
}

export class OAuthProviderException extends ClientException<
  never,
  "OAuthProviderException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("OAuthProviderException", message, null as never, options)
  }
}

export class ThatchVerificationFailedException extends ClientException<
  never,
  "ThatchVerificationFailedException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("ThatchVerificationFailedException", message, null as never, options)
  }
}

export class ThatchAuthenticationException extends ClientException<
  never,
  "ThatchAuthenticationException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("ThatchAuthenticationException", message, null as never, options)
  }
}

export class EmailAliasException extends ClientException<
  never,
  "EmailAliasException"
> {
  constructor(message: string, options?: ErrorOptions) {
    super("EmailAliasException", message, null as never, options)
  }
}
